import { useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Loader2, Sparkles, Target } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import ExportButtons from "./ExportButtons";
import CopyLessonButton from "./CopyLessonButton";
import LessonRefineChat from "./LessonRefineChat";
import LessonPackageButton from "./LessonPackageButton";
import StudentMaterialsSection from "./StudentMaterialsSection";
import SuccessCriteriaSection from "./SuccessCriteriaSection";
import DiagramImage from "./DiagramImage";
import { reorderLessonMarkdown } from "../lib/reorderLessonMarkdown";
import { extractSuccessCriteria } from "../lib/successCriteria";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const meta = (inputs) => {
  if (!inputs) return [];
  const order = ["grade", "subject", "time", "activity_type", "skill_focus"];
  return order
    .map((k) => (inputs[k] ? { key: k, value: inputs[k] } : null))
    .filter(Boolean);
};

export const markdownComponents = {
  h1: ({ node, ...props }) => (
    <h1
      className="font-black text-3xl sm:text-4xl text-[#4a6984] tracking-tight mt-8 first:mt-0"
      {...props}
    />
  ),
  h2: ({ node, ...props }) => (
    <h2
      className="font-extrabold text-2xl text-[#4a6984] tracking-tight mt-10 pb-2 border-b border-[#4a6984]/15"
      {...props}
    />
  ),
  h3: ({ node, ...props }) => (
    <h3
      className="font-bold text-lg text-[#4a6984] tracking-tight mt-6"
      {...props}
    />
  ),
  h4: ({ node, ...props }) => (
    <h4
      className="font-bold text-base text-[#4a6984] uppercase tracking-[0.18em] mt-5"
      {...props}
    />
  ),
  p: ({ node, ...props }) => (
    <p className="text-[#4a6984] font-medium leading-relaxed my-3" {...props} />
  ),
  ul: ({ node, ...props }) => (
    <ul className="my-3 space-y-1.5 list-none" {...props} />
  ),
  ol: ({ node, ...props }) => (
    <ol className="my-3 space-y-1.5 list-decimal list-outside pl-5 marker:text-[#96ded1] marker:font-extrabold" {...props} />
  ),
  li: ({ node, ordered, ...props }) =>
    ordered ? (
      <li className="text-[#4a6984] font-medium leading-relaxed pl-1" {...props} />
    ) : (
      <li className="flex items-start text-[#4a6984] font-medium leading-relaxed">
        <span className="ae-marker" />
        <span {...props} />
      </li>
    ),
  strong: ({ node, ...props }) => (
    <strong className="text-[#1c2d3d] font-extrabold" {...props} />
  ),
  em: ({ node, ...props }) => (
    <em className="text-[#4a6984]/85 italic" {...props} />
  ),
  blockquote: ({ node, ...props }) => (
    <blockquote
      className="my-4 pl-4 border-l-4 border-[#96ded1] bg-[#96ded1]/10 py-3 pr-4 rounded-r-md text-[#1c2d3d]"
      {...props}
    />
  ),
  hr: () => <hr className="my-6 border-t border-[#4a6984]/15" />,
  code: ({ inline, node, ...props }) =>
    inline ? (
      <code
        className="px-1.5 py-0.5 rounded bg-[#f4f5f7] border border-[#4a6984]/10 text-[#1c2d3d] text-[0.9em] font-mono"
        {...props}
      />
    ) : (
      <pre className="my-4 p-4 bg-[#f4f5f7] border border-[#4a6984]/10 rounded-lg overflow-x-auto text-sm text-[#1c2d3d]">
        <code {...props} />
      </pre>
    ),
  table: ({ node, ...props }) => (
    <div className="my-5 overflow-x-auto rounded-lg border border-[#4a6984]/15">
      <table className="w-full border-collapse text-sm" {...props} />
    </div>
  ),
  thead: ({ node, ...props }) => <thead className="bg-[#f4f5f7]" {...props} />,
  th: ({ node, ...props }) => (
    <th
      className="text-left font-extrabold uppercase tracking-[0.12em] text-xs text-[#4a6984] p-3 border-b border-[#4a6984]/15"
      {...props}
    />
  ),
  td: ({ node, ...props }) => (
    <td className="p-3 align-top text-[#4a6984] font-medium leading-relaxed border-b border-[#4a6984]/10" {...props} />
  ),
  a: ({ node, ...props }) => (
    <a
      className="text-[#1c2d3d] font-semibold underline underline-offset-4 hover:text-[#4a6984]"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    />
  ),
  img: ({ node, ...props }) => <DiagramImage {...props} />,
};

export const LessonDocument = ({ activity, onActivityUpdate, enableRefine = true, hideDownloads = false }) => {
  const [view, setView] = useState("lesson"); // "lesson" | "script"
  // Phase 144.B — surgical refresh state. Re-generates ONLY the
  // teacher_script_markdown in the new Moves-First format and keeps
  // every other lesson artefact intact.
  const [refreshing, setRefreshing] = useState(false);

  const handleRefreshTeacherPlan = async () => {
    if (!activity?.id) return;
    setRefreshing(true);
    try {
      const { data } = await axios.post(
        `${API}/activities/${activity.id}/refresh-teacher-plan`,
        {},
        {
          withCredentials: true,
          // The refresh kicks off a fresh Claude call which can take
          // 30-90s wall-clock. Default axios timeout is too short and
          // the Cloudflare edge sometimes 502s before the upstream
          // responds — give the browser its own generous ceiling.
          timeout: 180000,
        },
      );
      if (data?.teacher_script_markdown && onActivityUpdate) {
        onActivityUpdate({
          ...activity,
          teacher_script_markdown: data.teacher_script_markdown,
          teacher_script_refreshed_at: new Date().toISOString(),
        });
      }
      toast.success("Teacher Plan refreshed", {
        description: "Now in the new Moves-First layout.",
      });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Refresh failed.";
      toast.error("Couldn't refresh the Teacher Plan", {
        description: String(detail),
      });
    } finally {
      setRefreshing(false);
    }
  };
  // Phase 85a — summative tasks save their markdown under
  // `summative_markdown` (not `lesson_markdown`). Fall back to it so the
  // existing renderer handles them without bespoke wiring.
  const effectiveMarkdown =
    activity?.lesson_markdown || activity?.summative_markdown || "";
  // Reorder so exit-ticket + printable sections always sit at the end of
  // the lesson page (matches the PDF/Word print order so the on-screen
  // experience and the printed stack stay in sync).
  const reorderedLessonMarkdown = useMemo(
    () => reorderLessonMarkdown(effectiveMarkdown),
    [effectiveMarkdown]
  );
  // Pull the Success Criteria table out for the Achievement Chart
  // toggle. When the lesson doesn't include a structured criteria
  // table (e.g. older lessons, supply plans), `criteriaSplit` stays
  // null and we fall through to the unchanged ReactMarkdown render.
  const criteriaSplit = useMemo(
    () => extractSuccessCriteria(reorderedLessonMarkdown),
    [reorderedLessonMarkdown]
  );
  if (!activity) return null;
  const isSupply = activity.output_type === "supply_plan";
  const isSummativeTask = activity.output_type === "summative_task";
  const hasScript = !!(activity.teacher_script_markdown && !isSupply && !isSummativeTask);
  const tags = [
    ...(activity.transferable_skills || []),
    ...(activity.ontario_learning_skills || []),
  ];
  const chips = meta(activity.inputs);

  return (
    <article
      data-testid="lesson-document"
      className="ae-card shadow-xl shadow-[#4a6984]/5 p-6 md:p-12"
    >
      <header className="border-b border-[#4a6984]/10 pb-8 mb-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 md:flex-wrap">
          <div className="min-w-0">
            <p className="font-bold uppercase tracking-[0.25em] text-xs text-[#4a6984]/70">
              {isSupply ? "Emergency Supply Plan" : isSummativeTask ? "Summative Task" : "Generated Lesson"}
            </p>
            <h2
              data-testid="activity-name"
              className="font-black text-3xl sm:text-4xl text-[#4a6984] mt-3 tracking-tight"
            >
              {activity.activity_name || "Untitled Lesson"}
            </h2>
            {activity.tagline && (
              <p
                data-testid="activity-tagline"
                className="mt-3 text-[#4a6984]/85 font-medium text-base sm:text-lg max-w-2xl"
              >
                {activity.tagline}
              </p>
            )}
          </div>
          {activity.id && !hideDownloads && (
            <div className="w-full md:w-auto md:shrink-0">
              <p className="font-bold uppercase text-[10px] tracking-[0.25em] text-[#4a6984]/60 mb-2">
                Export
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <ExportButtons
                  activityId={activity.id}
                  activityName={activity.activity_name}
                  hasScript={!!activity.teacher_script_markdown}
                  hasHandout={
                    !activity.output_type?.startsWith("math_") &&
                    /Student Handout|Exit Ticket/i.test(effectiveMarkdown || "")
                  }
                  // Phase 88 — Math Engine lessons that include a
                  // student-facing handout (worksheet / diagnostic /
                  // exit ticket section) now expose a separate
                  // "Worksheet (PDF)" button. Activation activities are
                  // teacher-facilitated so they keep no worksheet.
                  hasMathHandout={
                    activity.output_type?.startsWith("math_") &&
                    activity.output_type !== "math_activation" &&
                    activity.output_type !== "math_unit_summary" &&
                    /Student Worksheet|Student Diagnostic|Exit Ticket|Student-Facing/i.test(effectiveMarkdown || "")
                  }
                  hasSkillEvidence={
                    Array.isArray(activity.student_sheets) &&
                    activity.student_sheets.some(
                      (s) => s && (s.sheet_type === "skill_exit_ticket" || s._skillsfacilitator_exit_ticket)
                    )
                  }
                  hasReading={!!activity.reading_markdown}
                  readingTextType={activity.reading_text_type || null}
                  size="sm"
                  onlyKeys={
                    isSummativeTask
                      ? ["pdf", "docx"]
                      : isSupply
                      ? ["pdf", "slides"]
                      : activity.output_type === "math_activation"
                      ? ["pdf", "docx", "slides"]
                      : activity.output_type === "math_diagnostic" ||
                        activity.output_type === "math_exit_review"
                      ? ["pdf", "worksheet", "docx"]
                      : activity.output_type === "math_unit_summary"
                      ? ["pdf", "docx"]
                      : activity.output_type === "math_lesson1" ||
                        activity.output_type === "math_lesson2"
                      ? ["pdf", "worksheet", "docx", "slides"]
                      : undefined
                  }
                />
                {!isSupply && (
                  <CopyLessonButton activity={activity} size="sm" testid="lesson-copy" />
                )}
              </div>
              {/* Phase 103 — Combined Lesson Package primary CTA, only
                  for lesson activities (not units, not supply plans,
                  not summative tasks, not the teacher-facilitated
                  math_activation / math_unit_summary types which have
                  no student-facing material to package). */}
              {!isSupply && !isSummativeTask && view === "lesson"
                && activity.output_type !== "math_activation"
                && activity.output_type !== "math_unit_summary" && (
                <div className="mt-3" data-testid="lesson-package-cta-top">
                  <LessonPackageButton activity={activity} variant="compact" />
                </div>
              )}
            </div>
          )}
        </div>

        {(chips.length > 0 || tags.length > 0) && (
          <div className="mt-5 flex flex-wrap gap-2" data-testid="lesson-chips">
            {chips.map((c) => (
              <span key={c.key} className="ae-pill">
                {c.value}
              </span>
            ))}
            {tags.slice(0, 8).map((t, i) => (
              <span
                key={`${t}-${i}`}
                className="ae-pill !bg-[#96ded1]/25 !border-[#96ded1]/60 !text-[#1c2d3d]"
                data-testid={`lesson-tag-${i}`}
              >
                {t}
              </span>
            ))}
          </div>
        )}
      </header>

      {hasScript && (
        <div
          data-testid="lesson-doc-tabs"
          className="mt-2 mb-6 flex items-center gap-2 border-b border-[#4a6984]/10"
        >
          <button
            type="button"
            data-testid="tab-lesson-plan"
            onClick={() => setView("lesson")}
            className={
              "px-4 py-2 -mb-px font-extrabold uppercase tracking-[0.12em] text-xs transition-colors " +
              (view === "lesson"
                ? "text-[#4a6984] border-b-2 border-[#96ded1]"
                : "text-[#4a6984]/55 hover:text-[#4a6984]")
            }
          >
            Lesson Plan
          </button>
          <button
            type="button"
            data-testid="tab-teacher-script"
            onClick={() => setView("script")}
            className={
              "px-4 py-2 -mb-px font-extrabold uppercase tracking-[0.12em] text-xs transition-colors " +
              (view === "script"
                ? "text-[#4a6984] border-b-2 border-[#96ded1]"
                : "text-[#4a6984]/55 hover:text-[#4a6984]")
            }
          >
            Teacher Script
          </button>
          <span className="ml-auto text-[10px] text-[#4a6984]/55 font-semibold uppercase tracking-[0.18em] pb-2">
            {view === "script" ? "Hold this while you run the lesson" : "For planning & sharing"}
          </span>
        </div>
      )}

      {view === "script" && hasScript ? (
        <div data-testid="teacher-script-markdown" className="ae-prose">
          {/* Phase 144.B — Refresh banner. Surfaced when the saved
              teacher script lacks the new Moves-First primary block.
              Old lessons authored before Phase 144 are detected by
              the absence of "TEACHER MOVES" in the markdown body —
              once refreshed, the banner disappears. */}
          {!/TEACHER MOVES/i.test(activity.teacher_script_markdown || "") && (
            <div
              data-testid="teacher-plan-refresh-banner"
              className="not-prose mb-6 rounded-lg border border-[#96ded1]/60 bg-[#96ded1]/15 px-4 py-3 sm:px-5 sm:py-4 flex flex-col sm:flex-row sm:items-center gap-3"
            >
              <div className="flex-1 min-w-0">
                <p className="font-extrabold uppercase tracking-[0.18em] text-[10px] text-[#1c2d3d]/85 inline-flex items-center gap-1.5">
                  <Sparkles className="h-3 w-3" /> New layout available
                </p>
                <p className="mt-1 text-sm font-semibold text-[#1c2d3d] leading-snug">
                  This Teacher Plan is in the old layout. Refresh it to
                  pull the new Moves-First format with the three
                  colour-coded look-for columns and an optional Sample
                  Teacher Talk appendix.
                </p>
              </div>
              <button
                type="button"
                onClick={handleRefreshTeacherPlan}
                disabled={refreshing}
                data-testid="teacher-plan-refresh-btn"
                className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md font-extrabold text-sm bg-[#1c2d3d] text-white hover:bg-[#0f1e2c] disabled:opacity-60 transition-colors shrink-0"
              >
                {refreshing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Refreshing&hellip;
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    Refresh Teacher Plan
                  </>
                )}
              </button>
            </div>
          )}
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {activity.teacher_script_markdown}
          </ReactMarkdown>
        </div>
      ) : effectiveMarkdown ? (
        <div data-testid="lesson-markdown" className="ae-prose">
          {activity.skill_lookfor_attribution?.text && (
            <div
              data-testid="skill-lookfor-attribution"
              className="not-prose mb-5 flex items-start gap-2.5 rounded-lg border border-[#96ded1]/70 bg-[#96ded1]/15 px-4 py-3"
            >
              <Target className="mt-0.5 h-4 w-4 shrink-0 text-[#1c2d3d]" />
              <p className="text-sm font-medium leading-relaxed text-[#1c2d3d]">
                {activity.skill_lookfor_attribution.text}
              </p>
            </div>
          )}
          {criteriaSplit ? (
            <>
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {criteriaSplit.before}
              </ReactMarkdown>
              <SuccessCriteriaSection
                headingText={criteriaSplit.headingText}
                rawTable={criteriaSplit.rawTable}
                criteria={criteriaSplit.criteria}
              />
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {criteriaSplit.after}
              </ReactMarkdown>
            </>
          ) : (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {reorderedLessonMarkdown}
            </ReactMarkdown>
          )}
        </div>
      ) : (
        <p className="text-[#4a6984]/70 italic">
          This activity has no rendered lesson document.
        </p>
      )}

      {/* Reading Generator output (Feb 2026) — student reading + words
          to know + four-section response block. Appears below the
          lesson when the teacher ticked "Include Reading" on the form
          and the second Claude call succeeded. */}
      {view === "lesson" && activity.reading_markdown && (
        <section
          data-testid="reading-block"
          className="mt-12 pt-10 border-t-2 border-dashed border-[#96ded1]/60"
        >
          <div className="mb-6 flex items-center gap-2">
            <span
              data-testid="reading-block-eyebrow"
              className="inline-block px-3 py-1 rounded-full bg-[#96ded1]/30 text-[#1c2d3d] text-[10px] font-extrabold uppercase tracking-[0.2em]"
            >
              Reading + Response
            </span>
            {activity.reading_text_type && (
              <span
                data-testid="reading-text-type-pill"
                className="inline-block px-3 py-1 rounded-full bg-[#f4f5f7] text-[#4a6984] text-[10px] font-bold uppercase tracking-[0.18em] border border-[#4a6984]/15"
              >
                {String(activity.reading_text_type).replace(/_/g, " ")}
              </span>
            )}
            {activity.reading_mode === "support" && (
              <span
                data-testid="reading-mode-support-pill"
                className="inline-block px-3 py-1 rounded-full bg-[#96ded1]/25 text-[#1c2d3d] text-[10px] font-bold uppercase tracking-[0.18em] border border-[#96ded1]/60"
                title="Support mode — shorter reading (150-350 words) with 2-question multiple-choice comprehension check"
              >
                Support
              </span>
            )}
          </div>
          <div data-testid="reading-markdown" className="ae-prose">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {activity.reading_markdown}
            </ReactMarkdown>
          </div>
        </section>
      )}

      {/* Phase 101 — Student Sheet PDF Bundle. Renders after the
          lesson body (and after the reading block if present) so the
          teacher sees the per-sheet inventory + one combined-PDF
          download immediately below their lesson plan. Hidden for
          activities without student_sheets (legacy lessons, supply
          plans, summative tasks). */}
      {!hideDownloads && view === "lesson" && !isSupply && !isSummativeTask && (
        <StudentMaterialsSection activity={activity} />
      )}

      {/* Phase 103 — bottom-of-document primary CTA, mirrors the
          compact one near the top. The teacher can grab the package
          from either end of the document. */}
      {!hideDownloads && !isSupply && !isSummativeTask && view === "lesson"
        && activity.output_type !== "math_activation"
        && activity.output_type !== "math_unit_summary" && (
        <div
          className="mt-12 pt-10 border-t-2 border-dashed border-[#1c2d3d]/20 flex flex-col items-start gap-3"
          data-testid="lesson-package-cta-bottom"
        >
          <div>
            <p className="font-bold uppercase text-[10px] tracking-[0.25em] text-[#4a6984]/60 mb-1">
              Print + share
            </p>
            <p className="text-[#4a6984] text-sm max-w-xl">
              Download one PDF with every piece of this lesson — plan, teacher
              script, reading handout, and student sheets — print only the
              pages you need.
            </p>
          </div>
          <LessonPackageButton activity={activity} variant="primary" />
        </div>
      )}

      {enableRefine && activity.id && !isSupply && !isSummativeTask && view === "lesson" && (
        <LessonRefineChat activity={activity} onActivityUpdate={onActivityUpdate} />
      )}
    </article>
  );
};

export default LessonDocument;
